import { useEffect, useMemo, useRef, useState } from 'react';
import { withStyles, usePagination } from 'vopay';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { Button, Paginator, Table, TableFiltersContainer } from '#shared/components/UI';
import useClientAccounts from '#shared/hooks/useApi/useClientAccounts';
import useFetchClientAccountBalanceRange from '#shared/hooks/useApi/useFetchClientAccountBalanceRange';
import ClientAccountListProvider from './ClientAccountList.provider';
import { ClientAccountListHostProps } from './types';
import { FormValues, useFilterFormConfig } from './useFilterFormConfig';
import useTableConfig from './useTableConfig';
import styles from './ClientAccountList.module.scss';

const cn = withStyles(styles);

const PAGE_SIZE = 10;

// The Client Account List feature root (ID-13350). Owns the filters + table + paginator that used to live
// inside client-dashboard's ClientAccountsPage Card; the host still provides the page header and the white
// Card around this. Mounts the feature-local provider so internal children (the table config) read
// account/hostPaths from context.
export default function ClientAccountList({ account, hostPaths }: ClientAccountListHostProps) {
  return (
    <ClientAccountListProvider account={account} hostPaths={hostPaths}>
      <ClientAccountListContent />
    </ClientAccountListProvider>
  );
}

function ClientAccountListContent() {
  const { pagination, clientAccounts, fetchClientAccounts } = useClientAccounts();
  const { balanceRange, fetchClientAccountBalanceRange } = useFetchClientAccountBalanceRange();
  const tableConfig = useTableConfig(clientAccounts);
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const overflowRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atScrollStart, setAtScrollStart] = useState(false);
  const [atScrollEnd, setAtScrollEnd] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.ClientAccountList' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });

  useEffect(() => {
    const ref = overflowRef.current;

    if (!ref) return;

    const obs = new MutationObserver(() => {
      setHasOverflow(ref.scrollWidth > ref.clientWidth);
      handleScroll();
    });

    obs.observe(ref, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    const handleScroll = () => {
      setAtScrollStart(ref.scrollLeft < 10);
      setAtScrollEnd(Math.abs(ref.clientWidth + ref.scrollLeft - ref.scrollWidth) < 10);
    };

    ref.addEventListener('scroll', handleScroll);

    return () => {
      obs.disconnect();
      ref.removeEventListener('scroll', handleScroll);
    };
  }, [overflowRef.current]);

  // Balance range feeds the filter's slider bounds and default. It's a blocking fetch on the client list
  // today (it raised the loader through useDataFetchIndicator), so keep it blocking here.
  useEffect(() => {
    async function loadBalanceRange() {
      // TODO(ID-follow-up): replace the fullscreen blocking loader with a localized table skeleton
      // (loading-conventions) in the next ticket — this preserves today's behaviour, it isn't the target.
      blockingLoader.show();

      try {
        await fetchClientAccountBalanceRange();
      } catch {
        // useFetchClientAccountBalanceRange already logged; the slider just stays hidden, as before.
      } finally {
        blockingLoader.hide();
      }
    }

    void loadBalanceRange();
  }, []);

  const { pageCount, pageIndex, setPageIndex } = usePagination(pagination.TotalRecords, {
    pageSize: PAGE_SIZE,
  });

  const { query, setQuery, filtersApplied } = useQuery();

  const { renderFormElements, ctx } = useFilterFormConfig({
    query,
    balanceRange,
    onSubmit: (values) => {
      setQuery(values);
    },
  });

  useEffect(() => {
    setPageIndex(0);
    refresh(0);
  }, [query]);

  async function refresh(offset: number) {
    const balanceRangeFilter = query.balance?.split('|') || [];

    // TODO(ID-follow-up): replace the fullscreen blocking loader with a localized table skeleton
    // (loading-conventions) in the next ticket — this preserves today's behaviour, it isn't the target.
    blockingLoader.show();

    try {
      await fetchClientAccounts({
        Offset: offset,
        Limit: PAGE_SIZE,
        SearchText: query.keyword,
        IsBusiness: query.clientType ? query.clientType === 'business' : undefined,
        Label: query.label,
        MinBalance: balanceRangeFilter[0] || undefined,
        MaxBalance: balanceRangeFilter[1] || undefined,
        AccountStatus: query.accountStatus ? query.accountStatus === 'active' : undefined,
      });
    } catch {
      // The hook already logged; surface it to the user.
      alertDialog.error(
        t('errorTitle', { defaultValue: 'Error' }),
        t('errorContent', { defaultValue: 'An error occurred while fetching client accounts' }),
        {
          abortLabel: tGlobal('close', { defaultValue: 'Close' }),
        },
      );
    } finally {
      blockingLoader.hide();
    }
  }

  return (
    <>
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <TableFiltersContainer.Trigger />

        <TableFiltersContainer.Content>
          <form className={cn('filtersInner')} onSubmit={ctx.handleSubmit}>
            {renderFormElements()}

            <Button type="submit" colorScheme="primary" size="small">
              {tGlobal('search', { defaultValue: 'Search' })}
            </Button>
          </form>
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <div
        className={cn(
          'tableContainerWithFade',
          hasOverflow && 'hasOverflow',
          atScrollStart && 'atScrollStart',
          atScrollEnd && 'atScrollEnd',
        )}
      >
        <div ref={overflowRef} className={cn('tableOverflowContainer')}>
          {/* Table here is vopay's Table2 (see #shared/components/UI) — inherently the v2 table, so no
              version prop; identical DOM to the host's old <Table version="2" /> dispatcher render. */}
          <Table {...tableConfig} />
        </div>
      </div>

      {pageCount > 1 && (
        <div className={cn('paginator')}>
          <Paginator
            pageCount={pageCount}
            onPageChange={(e) => {
              const newOffset = setPageIndex(e.selected);
              refresh(newOffset);
            }}
            pageOffset={pageIndex}
          />
        </div>
      )}
    </>
  );
}

function useQuery() {
  const { search, pathname } = useLocation();
  const history = useHistory();

  const query = useMemo(() => {
    return Object.fromEntries(new URLSearchParams(search)) as Omit<FormValues, 'balance'> & {
      balance: string;
    };
  }, [search]);

  const filtersApplied = useMemo(() => Object.keys(query).length > 0, [query]);

  function setQuery(data: FormValues) {
    const newParams = new URLSearchParams(
      Object.entries(data).reduce((acc, [key, val]) => {
        if (!val) return acc;

        return {
          ...acc,
          [key]: val,
        };
      }, {}),
    );

    history.replace({ pathname, search: newParams.toString() });
  }

  return {
    query,
    setQuery,
    filtersApplied,
  };
}
